/**
 * =============================================================================
 * PRODUCT INFO CARD DECK - Premium Edition with GSAP
 * BrainyTots - Science-based toys that give joy to little minds
 * =============================================================================
 *
 * Features:
 * - GSAP-powered spring physics and elastic easing
 * - Buttery-smooth 60fps drag interactions
 * - Premium micro-interactions per card type
 * - Physics-based throw with momentum
 * - Touch and keyboard navigation
 * - Accessibility support
 */

class ProductCardDeck {
  constructor(element) {
    this.deck = element;
    this.viewport = element.querySelector('.bt-deck__viewport');
    this.cards = Array.from(element.querySelectorAll('.bt-deck__card'));
    this.dots = Array.from(element.querySelectorAll('.bt-deck__dot'));
    this.totalCards = this.cards.length;
    this.currentIndex = 0;

    // Touch tracking
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragCurrentX = 0;
    this.dragCurrentY = 0;
    this.dragStartTime = 0;
    this.velocityX = 0;
    this.lastX = 0;
    this.lastTime = 0;
    this.hasSwiped = false;

    // Physics constants
    this.SWIPE_THRESHOLD = 60;
    this.VELOCITY_THRESHOLD = 0.4;
    this.MAX_ROTATION = 18;
    this.THROW_MULTIPLIER = 1.5;

    // GSAP ease presets for premium feel
    this.SPRING_EASE = 'elastic.out(1, 0.5)';
    this.SMOOTH_EASE = 'power3.out';
    this.SNAP_EASE = 'back.out(1.4)';
    this.FLY_EASE = 'power2.in';

    // Bind methods
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.init();
  }

  init() {
    // Calculate and set dynamic height based on tallest card
    this.setDynamicHeight();

    // Set initial card positions with GSAP
    this.setupCards();

    // Touch events
    this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.viewport.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.viewport.addEventListener('touchend', this.handleTouchEnd, { passive: true });

    // Mouse events (for desktop)
    this.viewport.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);

    // Keyboard navigation
    this.deck.addEventListener('keydown', this.handleKeyDown);

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToCard(index));
    });

    // Make deck focusable
    this.deck.setAttribute('tabindex', '0');
    this.deck.setAttribute('role', 'region');
    this.deck.setAttribute('aria-label', 'Product information cards. Swipe or use arrow keys to navigate.');

    // Initial micro-interaction
    this.triggerCardEntrance();
  }

  // ===========================================================================
  // Dynamic Height Calculation
  // ===========================================================================

  setDynamicHeight() {
    // Temporarily make all cards visible and position: relative to measure natural height
    const originalStyles = this.cards.map(card => ({
      position: card.style.position,
      opacity: card.style.opacity,
      transform: card.style.transform,
      visibility: card.style.visibility
    }));

    // Reset cards to measure their natural content height
    this.cards.forEach(card => {
      card.style.position = 'relative';
      card.style.opacity = '1';
      card.style.transform = 'none';
      card.style.visibility = 'visible';
      card.style.height = 'auto';
    });

    // Find the tallest card
    let maxHeight = 0;
    this.cards.forEach(card => {
      const height = card.scrollHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    // Add some padding for the stack effect
    const finalHeight = maxHeight + 20;

    // Set the dynamic height as CSS custom property
    this.deck.style.setProperty('--deck-height', `${finalHeight}px`);

    // Restore original card styles (will be set properly by setupCards)
    this.cards.forEach((card, index) => {
      card.style.position = '';
      card.style.opacity = '';
      card.style.transform = '';
      card.style.visibility = '';
      card.style.height = '';
    });
  }

  // ===========================================================================
  // Card Setup with GSAP
  // ===========================================================================

  setupCards() {
    this.cards.forEach((card, index) => {
      const stackIndex = this.getStackIndex(index);

      gsap.set(card, {
        y: stackIndex * 8,
        z: stackIndex * -40,
        rotateX: stackIndex * 3,
        scale: 1 - stackIndex * 0.04,
        opacity: this.getOpacityForStack(stackIndex),
        zIndex: this.totalCards - stackIndex,
        transformOrigin: 'center bottom',
        force3D: true
      });

      card.setAttribute('aria-hidden', index !== this.currentIndex);
    });

    this.updateDots();
  }

  getStackIndex(cardIndex) {
    const diff = cardIndex - this.currentIndex;
    if (diff === 0) return 0;
    if (diff === 1 || diff === -(this.totalCards - 1)) return 1;
    if (diff === 2 || diff === -(this.totalCards - 2)) return 2;
    return 3; // Hidden
  }

  getOpacityForStack(stackIndex) {
    if (stackIndex === 0) return 1;
    if (stackIndex === 1) return 0.6;
    if (stackIndex === 2) return 0.3;
    return 0;
  }

  // ===========================================================================
  // Premium Card Entrance Animation
  // ===========================================================================

  triggerCardEntrance() {
    const activeCard = this.cards[this.currentIndex];

    // Stagger entrance of card content
    const icon = activeCard.querySelector('.bt-deck__icon');
    const title = activeCard.querySelector('.bt-deck__title');
    const content = activeCard.querySelector('.bt-deck__content');
    const extras = activeCard.querySelectorAll('.bt-deck__materials, .bt-deck__benefits, .bt-deck__items, .bt-deck__skills, .bt-deck__certifications, .bt-deck__age-bar');

    const tl = gsap.timeline();

    // Card pops in
    tl.fromTo(activeCard,
      { scale: 0.9, y: 30 },
      { scale: 1, y: 0, duration: 0.5, ease: this.SNAP_EASE }
    );

    // Icon bounces
    if (icon) {
      tl.fromTo(icon,
        { scale: 0, rotation: -20 },
        { scale: 1, rotation: 0, duration: 0.4, ease: this.SPRING_EASE },
        '-=0.3'
      );
    }

    // Title fades up
    if (title) {
      tl.fromTo(title,
        { opacity: 0, y: 10 },
        { opacity: 0.7, y: 0, duration: 0.3, ease: this.SMOOTH_EASE },
        '-=0.2'
      );
    }

    // Content fades up
    if (content) {
      tl.fromTo(content,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.3, ease: this.SMOOTH_EASE },
        '-=0.2'
      );
    }

    // Trigger card-specific micro-interaction
    this.triggerMicroInteraction(this.currentIndex);
  }

  // ===========================================================================
  // Touch/Mouse Handlers
  // ===========================================================================

  handleTouchStart(e) {
    if (e.touches.length > 1) return;
    this.startDrag(e.touches[0].clientX, e.touches[0].clientY);
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;

    const touch = e.touches[0];
    const diffX = Math.abs(touch.clientX - this.dragStartX);
    const diffY = Math.abs(touch.clientY - this.dragStartY);

    // Horizontal swipe - prevent scroll
    if (diffX > diffY && diffX > 10) {
      e.preventDefault();
      this.updateDrag(touch.clientX, touch.clientY);
    } else if (diffY > diffX && diffY > 10) {
      // Vertical scroll - cancel drag
      this.cancelDrag();
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;
    const touch = e.changedTouches[0];
    this.endDrag(touch.clientX);
  }

  handleMouseDown(e) {
    e.preventDefault();
    this.startDrag(e.clientX, e.clientY);
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    this.updateDrag(e.clientX, e.clientY);
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;
    this.endDrag(e.clientX);
  }

  // ===========================================================================
  // GSAP-Powered Drag Logic
  // ===========================================================================

  startDrag(x, y) {
    const activeCard = this.cards[this.currentIndex];
    if (!activeCard) return;

    this.isDragging = true;
    this.dragStartX = x;
    this.dragStartY = y;
    this.dragCurrentX = x;
    this.lastX = x;
    this.lastTime = Date.now();
    this.velocityX = 0;
    this.dragStartTime = Date.now();

    // Kill any ongoing animations on this card
    gsap.killTweensOf(activeCard);

    // Slight lift on grab
    gsap.to(activeCard, {
      scale: 1.03,
      boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 10px 30px rgba(0,0,0,0.15)',
      duration: 0.2,
      ease: this.SMOOTH_EASE
    });
  }

  updateDrag(x, y) {
    this.dragCurrentX = x;
    const activeCard = this.cards[this.currentIndex];
    if (!activeCard) return;

    // Calculate velocity for momentum
    const now = Date.now();
    const dt = now - this.lastTime;
    if (dt > 0) {
      this.velocityX = (x - this.lastX) / dt;
    }
    this.lastX = x;
    this.lastTime = now;

    const diffX = x - this.dragStartX;
    const rotation = (diffX / this.viewport.offsetWidth) * this.MAX_ROTATION;
    const lift = Math.min(Math.abs(diffX) / 8, 25);

    // Smooth follow with GSAP
    gsap.to(activeCard, {
      x: diffX,
      y: -lift,
      rotation: rotation,
      duration: 0.1,
      ease: 'power1.out',
      overwrite: 'auto'
    });

    // Fade based on distance
    const progress = Math.abs(diffX) / (this.viewport.offsetWidth * 0.5);
    gsap.to(activeCard, {
      opacity: Math.max(0.4, 1 - progress * 0.6),
      duration: 0.1,
      overwrite: 'auto'
    });
  }

  cancelDrag() {
    this.isDragging = false;
    this.returnCard();
  }

  endDrag(endX) {
    this.isDragging = false;
    const activeCard = this.cards[this.currentIndex];
    if (!activeCard) return;

    const diffX = endX - this.dragStartX;
    const absVelocity = Math.abs(this.velocityX);

    // Determine if swipe should complete
    const shouldSwipe = Math.abs(diffX) > this.SWIPE_THRESHOLD || absVelocity > this.VELOCITY_THRESHOLD;

    if (shouldSwipe) {
      const direction = diffX > 0 ? 'right' : 'left';
      this.throwCard(direction, this.velocityX);
    } else {
      this.returnCard();
    }
  }

  // ===========================================================================
  // Premium Card Animations
  // ===========================================================================

  throwCard(direction, velocity) {
    const activeCard = this.cards[this.currentIndex];
    if (!activeCard) return;

    // Mark as swiped for hint removal
    if (!this.hasSwiped) {
      this.hasSwiped = true;
      this.deck.classList.add('has-swiped');
    }

    // Calculate throw distance based on velocity
    const throwDistance = direction === 'left' ? -120 : 120;
    const throwRotation = direction === 'left' ? -35 : 35;
    const velocityBoost = Math.min(Math.abs(velocity) * this.THROW_MULTIPLIER * 100, 50);

    // Fly away animation
    gsap.to(activeCard, {
      x: `${throwDistance + (direction === 'left' ? -velocityBoost : velocityBoost)}%`,
      y: -40,
      rotation: throwRotation,
      opacity: 0,
      scale: 0.9,
      duration: 0.4,
      ease: this.FLY_EASE,
      onComplete: () => {
        this.advanceToNext(direction);
      }
    });
  }

  returnCard() {
    const activeCard = this.cards[this.currentIndex];
    if (!activeCard) return;

    // Snap back with spring physics
    gsap.to(activeCard, {
      x: 0,
      y: 0,
      rotation: 0,
      scale: 1,
      opacity: 1,
      boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
      duration: 0.6,
      ease: this.SPRING_EASE
    });
  }

  advanceToNext(direction) {
    const oldCard = this.cards[this.currentIndex];

    // Reset old card position (it's now at back of deck)
    // Keep opacity 0 so it doesn't flash on screen - animateStackUpdate will fade it in
    gsap.set(oldCard, {
      x: 0,
      y: 24,
      z: -120,
      rotation: 0,
      rotateX: 9,
      scale: 0.88,
      opacity: 0,
      zIndex: 0
    });

    // Update index
    if (direction === 'left') {
      this.currentIndex = (this.currentIndex + 1) % this.totalCards;
    } else {
      this.currentIndex = (this.currentIndex - 1 + this.totalCards) % this.totalCards;
    }

    // Animate all cards to new positions
    this.animateStackUpdate();
    this.updateDots();
    this.announceCard();
  }

  animateStackUpdate() {
    this.cards.forEach((card, index) => {
      const stackIndex = this.getStackIndex(index);
      const isActive = stackIndex === 0;

      gsap.to(card, {
        y: stackIndex * 8,
        z: stackIndex * -40,
        rotateX: stackIndex * 3,
        scale: 1 - stackIndex * 0.04,
        opacity: this.getOpacityForStack(stackIndex),
        zIndex: this.totalCards - stackIndex,
        duration: 0.5,
        ease: this.SNAP_EASE,
        onComplete: () => {
          if (isActive) {
            this.triggerMicroInteraction(index);
          }
        }
      });

      card.setAttribute('aria-hidden', !isActive);
    });
  }

  goToCard(targetIndex) {
    if (targetIndex === this.currentIndex || targetIndex < 0 || targetIndex >= this.totalCards) return;

    const direction = targetIndex > this.currentIndex ? 'left' : 'right';
    const activeCard = this.cards[this.currentIndex];

    // Quick fly out
    gsap.to(activeCard, {
      x: direction === 'left' ? '-100%' : '100%',
      rotation: direction === 'left' ? -20 : 20,
      opacity: 0,
      duration: 0.3,
      ease: this.SMOOTH_EASE,
      onComplete: () => {
        gsap.set(activeCard, { x: 0, rotation: 0 });
        this.currentIndex = targetIndex;
        this.animateStackUpdate();
        this.updateDots();
        this.announceCard();
      }
    });
  }

  // ===========================================================================
  // Premium Micro-Interactions
  // ===========================================================================

  triggerMicroInteraction(index) {
    const card = this.cards[index];
    if (!card) return;

    const cardType = card.dataset.cardType;

    switch (cardType) {
      case 'materials':
        this.animateMaterials(card);
        break;
      case 'brain':
        this.animateBrain(card);
        break;
      case 'age':
        this.animateAge(card);
        break;
      case 'included':
        this.animateIncluded(card);
        break;
      case 'skills':
        this.animateSkills(card);
        break;
      case 'safety':
        this.animateSafety(card);
        break;
    }
  }

  animateMaterials(card) {
    const tags = card.querySelectorAll('.bt-deck__material-tag');

    gsap.fromTo(tags,
      { opacity: 0, y: 15, scale: 0.8 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.4,
        stagger: 0.08,
        ease: this.SNAP_EASE
      }
    );
  }

  animateBrain(card) {
    const benefits = card.querySelectorAll('.bt-deck__benefit');
    const icon = card.querySelector('.bt-deck__icon');

    // Pulse the icon
    gsap.fromTo(icon,
      { scale: 1 },
      { scale: 1.1, duration: 0.3, yoyo: true, repeat: 1, ease: 'power2.inOut' }
    );

    // Stagger benefits
    gsap.fromTo(benefits,
      { opacity: 0, x: -20 },
      {
        opacity: 1,
        x: 0,
        duration: 0.4,
        stagger: 0.1,
        ease: this.SMOOTH_EASE,
        delay: 0.2
      }
    );
  }

  animateAge(card) {
    const fill = card.querySelector('.bt-deck__age-fill');
    const emoji = card.querySelector('.bt-deck__age-emoji');

    if (fill) {
      // Get the target width from CSS variable
      const targetWidth = getComputedStyle(fill).getPropertyValue('--age-percent') || '50%';

      gsap.fromTo(fill,
        { width: '0%' },
        { width: targetWidth, duration: 0.8, ease: 'power2.out', delay: 0.2 }
      );
    }

    if (emoji) {
      gsap.fromTo(emoji,
        { scale: 0, rotation: -180 },
        { scale: 1, rotation: 0, duration: 0.5, ease: this.SPRING_EASE, delay: 0.8 }
      );
    }
  }

  animateIncluded(card) {
    const items = card.querySelectorAll('.bt-deck__item');
    const counts = card.querySelectorAll('.bt-deck__item-count[data-count]');

    // Pop in items
    gsap.fromTo(items,
      { opacity: 0, scale: 0.5, y: 20 },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: this.SPRING_EASE
      }
    );

    // Count up numbers
    counts.forEach((countEl, i) => {
      const target = parseInt(countEl.dataset.count, 10) || 1;
      const obj = { value: 0 };

      gsap.to(obj, {
        value: target,
        duration: 0.6,
        delay: 0.2 + i * 0.1,
        ease: 'power2.out',
        onUpdate: () => {
          countEl.textContent = Math.round(obj.value);
        }
      });
    });
  }

  animateSkills(card) {
    const skills = card.querySelectorAll('.bt-deck__skill');

    gsap.fromTo(skills,
      { opacity: 0, scale: 0, rotation: -15 },
      {
        opacity: 1,
        scale: 1,
        rotation: 0,
        duration: 0.4,
        stagger: 0.08,
        ease: this.SPRING_EASE
      }
    );
  }

  animateSafety(card) {
    const certs = card.querySelectorAll('.bt-deck__cert');
    const icon = card.querySelector('.bt-deck__icon svg');

    // Shield pulse
    if (icon) {
      gsap.fromTo(icon,
        { scale: 0.8 },
        { scale: 1, duration: 0.4, ease: this.SPRING_EASE }
      );
    }

    // Certs slide in with check animation
    gsap.fromTo(certs,
      { opacity: 0, x: -25 },
      {
        opacity: 1,
        x: 0,
        duration: 0.4,
        stagger: 0.12,
        ease: this.SMOOTH_EASE,
        delay: 0.2
      }
    );
  }

  // ===========================================================================
  // Navigation
  // ===========================================================================

  handleKeyDown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.goToCard((this.currentIndex - 1 + this.totalCards) % this.totalCards);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.goToCard((this.currentIndex + 1) % this.totalCards);
        break;
      case 'Home':
        e.preventDefault();
        this.goToCard(0);
        break;
      case 'End':
        e.preventDefault();
        this.goToCard(this.totalCards - 1);
        break;
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-pressed', isActive);

      // Animate active dot
      if (isActive) {
        gsap.fromTo(dot,
          { scale: 1 },
          { scale: 1.3, duration: 0.3, ease: this.SNAP_EASE }
        );
      } else {
        gsap.to(dot, { scale: 1, duration: 0.2 });
      }
    });
  }

  // ===========================================================================
  // Accessibility
  // ===========================================================================

  announceCard() {
    const card = this.cards[this.currentIndex];
    if (!card) return;

    const title = card.querySelector('.bt-deck__title');
    const content = card.querySelector('.bt-deck__content');

    let liveRegion = this.deck.querySelector('.bt-deck__live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'bt-deck__live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
      this.deck.appendChild(liveRegion);
    }

    const announcement = `Card ${this.currentIndex + 1} of ${this.totalCards}: ${title?.textContent || ''} - ${content?.textContent || ''}`;
    liveRegion.textContent = announcement;
  }
}

// ===========================================================================
// Description Accordion with GSAP
// ===========================================================================

class DeckDescriptionAccordion {
  constructor(element) {
    this.accordion = element;
    this.toggle = element.querySelector('.bt-deck-description__toggle');
    this.content = element.querySelector('.bt-deck-description__content');
    this.inner = element.querySelector('.bt-deck-description__inner');
    this.isOpen = false;

    this.init();
  }

  init() {
    // Set initial state
    gsap.set(this.content, { height: 0, opacity: 0 });

    this.toggle.addEventListener('click', () => this.toggleOpen());
    this.toggle.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleOpen();
      }
    });

    this.toggle.setAttribute('aria-expanded', 'false');
    this.content.id = this.content.id || `deck-desc-${Date.now()}`;
    this.toggle.setAttribute('aria-controls', this.content.id);
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.accordion.classList.toggle('is-open', this.isOpen);
    this.toggle.setAttribute('aria-expanded', this.isOpen);

    const icon = this.toggle.querySelector('.bt-deck-description__icon');

    if (this.isOpen) {
      // Measure content height
      const height = this.inner.offsetHeight;

      gsap.to(this.content, {
        height: height,
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out'
      });

      gsap.to(icon, {
        rotation: 180,
        duration: 0.3,
        ease: 'power2.out'
      });
    } else {
      gsap.to(this.content, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in'
      });

      gsap.to(icon, {
        rotation: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    }
  }
}

// ===========================================================================
// Auto-Initialize (wait for GSAP)
// ===========================================================================

let gsapRetryCount = 0;
const MAX_GSAP_RETRIES = 50; // 5 seconds total (50 * 100ms)

function initProductCardDecks() {
  // Check if GSAP is loaded
  if (typeof gsap === 'undefined') {
    gsapRetryCount++;
    if (gsapRetryCount <= MAX_GSAP_RETRIES) {
      console.warn(`ProductCardDeck: GSAP not loaded, retry ${gsapRetryCount}/${MAX_GSAP_RETRIES}...`);
      setTimeout(initProductCardDecks, 100);
    } else {
      console.error('ProductCardDeck: GSAP failed to load after maximum retries. Card animations disabled.');
      // Add fallback class to show cards statically
      document.querySelectorAll('.bt-deck').forEach((deck) => {
        deck.classList.add('bt-deck--no-animation');
      });
    }
    return;
  }

  // Reset retry count on successful load
  gsapRetryCount = 0;

  document.querySelectorAll('.bt-deck').forEach((deck) => {
    if (!deck.dataset.initialized) {
      new ProductCardDeck(deck);
      deck.dataset.initialized = 'true';
    }
  });

  document.querySelectorAll('.bt-deck-description').forEach((accordion) => {
    if (!accordion.dataset.initialized) {
      new DeckDescriptionAccordion(accordion);
      accordion.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardDecks);
} else {
  initProductCardDecks();
}

// Re-initialize for Shopify section rendering
document.addEventListener('shopify:section:load', initProductCardDecks);

// Export for external use
window.ProductCardDeck = ProductCardDeck;
window.DeckDescriptionAccordion = DeckDescriptionAccordion;
